/**
 * Per-device "opened" tracking for the closet wall.
 *
 * A closet glows when its front post hasn't been opened yet on this device.
 * State lives in localStorage only — intentionally NOT synced to the account
 * (see the build decision): no backend, ships fast, and the trade-off is that
 * the glow resolves per-device. Keyed by post id so re-publishing a new front
 * post in a drawer re-arms the glow automatically.
 */

const KEY = 'cc.openedPosts.v1';
// Defensive cap so a long-lived device can't grow the set unbounded. We keep
// the most-recently-opened ids; older ones falling off just means a closet
// might glow again, which is harmless.
const MAX_IDS = 500;

function read(): string[] {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    // Private mode / disabled storage / corrupt JSON — treat as "nothing opened".
    return [];
  }
}

function write(ids: string[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(ids.slice(-MAX_IDS)));
  } catch {
    // Storage unavailable — glow simply won't persist. Non-fatal.
  }
}

export function isOpened(postId: string | null | undefined): boolean {
  if (!postId) return false;
  return read().includes(postId);
}

/** Mark a post opened. Idempotent; moves the id to most-recent. */
export function markOpened(postId: string | null | undefined): void {
  if (!postId) return;
  const ids = read().filter((id) => id !== postId);
  ids.push(postId);
  write(ids);
}
